import { useCallback, useEffect, useRef } from "react";
import { useScrollbarWidth } from "./useScrollbarWidth";

/**
 * Hook to lock the scroll of the document when needed.
 * @returns The object containing the functions to
 * - lock
 * -  unlock the scroll of the document
 * - the function to check if the scroll of the document is locked.
 */
const useDocumentLockScrollY = (withPadding = false) => {
  const documentRef = useRef<HTMLElement | null>(null);
  const scrollTopRef = useRef<number>(0);
  const sbw = useScrollbarWidth();

  useEffect(() => {
    documentRef.current = document.documentElement;
  }, []);

  const lockDocumentScrollY = useCallback(() => {
    const $html = documentRef.current;
    if (!$html) return;

    scrollTopRef.current = $html.scrollTop || document.body.scrollTop;

    requestAnimationFrame(() => {
      $html.style.top = `${-scrollTopRef.current}px`;
      $html.classList.add("no-scroll");

      if (withPadding) {
        $html.style.paddingRight = `${sbw}px`;
      }
    });
  }, [sbw, withPadding]);

  const unlockDocumentScrollY = useCallback(() => {
    const $html = documentRef.current;
    if (!$html) return;

    const scrollTop = scrollTopRef.current;

    requestAnimationFrame(() => {
      $html.classList.remove("no-scroll");
      $html.style.removeProperty("top");

      if (withPadding) {
        $html.style.removeProperty("padding-right");
      }

      $html.scrollTop = scrollTop;
      document.body.scrollTop = scrollTop;
    });
  }, [withPadding]);

  const checkIsLockDocumentScrollY = useCallback(() => {
    const $html = documentRef.current;
    return $html?.classList.contains("no-scroll") ?? false;
  }, []);

  return {
    lockDocumentScrollY,
    unlockDocumentScrollY,
    checkIsLockDocumentScrollY,
  };
};

export default useDocumentLockScrollY;
